import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import LeadForm from '../components/LeadForm';
import { api } from '../lib/api';
import { TREATMENT_SLUGS, CITY_SLUGS, CATEGORIES } from '../data/categories';

export default function CityTreatment() {
  const { kategori, by } = useParams();
  const treatmentName = TREATMENT_SLUGS[kategori];
  const cityName = CITY_SLUGS[by];
  const parentCat = CATEGORIES.find((c) => c.treatments.includes(treatmentName));
  const [clinics, setClinics] = useState([]);

  useEffect(() => { api.get('/clinics/public').then((r) => setClinics(r.data.clinics || [])); }, []);

  if (!treatmentName || !cityName) {
    return <Layout><div className="p-16 text-center">Siden ble ikke funnet.</div></Layout>;
  }

  const cityClinics = clinics.filter((c) => c.treatments.includes(treatmentName) && c.city === cityName);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Hjem', item: 'https://specialists.no/' },
      { '@type': 'ListItem', position: 2, name: 'Behandlinger', item: 'https://specialists.no/behandlinger/' + (parentCat?.slug || '') },
      { '@type': 'ListItem', position: 3, name: `${treatmentName} ${cityName}`, item: `https://specialists.no/behandlinger/${kategori}/${by}` },
    ],
  };

  return (
    <Layout>
      <Seo
        title={`${treatmentName} ${cityName} – Klinikker med 4+ stjerner | Specialists.no`}
        description={`Sammenlign ${treatmentName.toLowerCase()}-klinikker i ${cityName}. Kun klinikker med 4+ stjerner. Gratis henvendelse, svar innen 24 timer.`}
        canonical={`https://specialists.no/behandlinger/${kategori}/${by}`}
        jsonLd={jsonLd}
      />
      <section className="bg-[#0A1F44] text-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-white/50 mb-3">
            <Link to="/" className="hover:text-white">Hjem</Link> / <Link to={`/behandlinger/${parentCat?.slug || 'estetikk-og-kosmetikk'}`} className="hover:text-white">{parentCat?.name || 'Behandlinger'}</Link> / <span className="text-white">{cityName}</span>
          </nav>
          <h1 className="font-display text-4xl lg:text-5xl font-bold">{treatmentName} i {cityName} — Finn din spesialist</h1>
          <p className="text-white/70 mt-4 text-lg max-w-3xl leading-relaxed">
            Leter du etter {treatmentName.toLowerCase()} i {cityName}? Specialists.no har samlet et utvalg av de best vurderte private klinikkene i området — alle med minst 4 stjerner på Google eller Trustpilot.
            Behandlingen utføres av medisinsk kvalifisert personell, og vi sørger for at forespørselen din når klinikken innen få minutter.
            Fyll ut skjemaet under, så kontakter en klinikk deg innen 24 timer med pris, ledige tider og individuell vurdering.
            Tjenesten er helt gratis og uforpliktende.
          </p>
        </div>
      </section>
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_1fr] gap-10">
          <div>
            <h2 className="font-display text-2xl font-bold text-[#0A1F44] mb-5">Klinikker i {cityName}</h2>
            {cityClinics.length === 0 ? (
              <div className="bg-white rounded-xl p-6 border border-[#0A1F44]/10 text-[#64748B]">
                Vi jobber med å signe flere partnerklinikker i {cityName}. I mellomtiden kan vi foreslå nærmeste anbefalte klinikk — fyll ut skjemaet.
              </div>
            ) : (
              <div className="space-y-3">
                {cityClinics.map((c) => (
                  <Link key={c.id} to={`/klinikker/${c.id}`} className="block bg-white rounded-xl p-5 border border-[#0A1F44]/10 hover:border-[#0A1F44]/30">
                    <div className="font-display text-lg font-bold text-[#0A1F44]">{c.name}</div>
                    <div className="text-sm text-[#64748B] mt-1">{c.location} · ⭐ {c.rating} ({c.reviews})</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div>
            <LeadForm initialTreatment={treatmentName} initialCategory={parentCat?.name} initialCity={cityName} clinics={clinics} compact />
          </div>
        </div>
      </section>
    </Layout>
  );
}
